import { useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2FormGrid,
  V2FormField,
  LoadingState,
  StatePanel,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPatch, apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { Dimension, DimensionValue } from '../../lib/types/finance.js';

interface ListResponse<T> {
  readonly items: readonly T[];
}

/** Standard detail levels offered one click away (SOT §12.3). */
const STANDARD_DIMENSIONS = [
  { code: 'CC', name: 'مرکز هزینه' },
  { code: 'PRJ', name: 'پروژه' },
  { code: 'BR', name: 'شعبه' },
  { code: 'CNT', name: 'قرارداد' },
] as const;

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html — coding / analytical dimensions
 * archetype: ENTITY_LIST + inline ENTITY_CREATE
 * status: KIT_CONFORMANT — real master-data endpoints
 *   (`/master-data/dimensions`, `/master-data/dimension-values`). A
 *   dimension is a floating detail level (تفصیلی شناور): cost centers,
 *   projects, branches, contracts are its values. Which accounts accept a
 *   level is configured on the account page; journal lines then pick values.
 * state reference: ready · empty · permission_denied · network_error
 */
export function DimensionsPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dimCode, setDimCode] = useState('');
  const [dimName, setDimName] = useState('');
  const [valueCode, setValueCode] = useState('');
  const [valueName, setValueName] = useState('');

  const dimensions = useApiGet<ListResponse<Dimension>>(
    slug ? `${base}/api/v1/master-data/dimensions?limit=200` : null,
    [slug, refreshKey],
  );
  const activeDimension = useMemo(() => {
    if (dimensions.status !== 'ready') return undefined;
    return (
      dimensions.data.items.find((d) => d.dimensionId === selectedId) ?? dimensions.data.items[0]
    );
  }, [dimensions, selectedId]);
  const values = useApiGet<ListResponse<DimensionValue>>(
    slug && activeDimension
      ? `${base}/api/v1/master-data/dimension-values?dimensionId=${activeDimension.dimensionId}&limit=200`
      : null,
    [slug, activeDimension?.dimensionId, refreshKey],
  );

  if (!slug) return null;

  const existingCodes = new Set(
    dimensions.status === 'ready' ? dimensions.data.items.map((d) => d.code) : [],
  );

  async function createDimension(code: string, name: string) {
    setError(null);
    setBusy(true);
    try {
      const created = await apiPost<Dimension>(`${base}/api/v1/master-data/dimensions`, {
        code,
        name,
      });
      setSelectedId(created.dimensionId);
      setDimCode('');
      setDimName('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'تعریف سطح تفصیلی ناموفق بود — کد تکراری است؟'));
    } finally {
      setBusy(false);
    }
  }

  async function onCreateDimension(event: FormEvent) {
    event.preventDefault();
    if (!dimCode.trim() || !dimName.trim()) {
      setError('کد و نام سطح تفصیلی الزامی است.');
      return;
    }
    await createDimension(dimCode.trim(), dimName.trim());
  }

  async function onCreateValue(event: FormEvent) {
    event.preventDefault();
    if (!activeDimension) return;
    if (!valueCode.trim() || !valueName.trim()) {
      setError('کد و نام الزامی است.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await apiPost(`${base}/api/v1/master-data/dimension-values`, {
        dimensionId: activeDimension.dimensionId,
        code: valueCode.trim(),
        name: valueName.trim(),
      });
      setValueCode('');
      setValueName('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'ثبت ناموفق بود — کد تکراری است؟'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleValue(value: DimensionValue) {
    setError(null);
    try {
      await apiPatch(`${base}/api/v1/master-data/dimension-values/${value.dimensionValueId}`, {
        status: value.status === 'active' ? 'inactive' : 'active',
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(bannerMessageFrom(err, 'تغییر وضعیت ناموفق بود.'));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
        مراکز هزینه، پروژه‌ها و سایر سطوح تفصیلی شناور. برای استفاده در اسناد، در صفحهٔ هر حساب معین
        سطوح مجاز آن حساب را فعال کنید.
      </div>

      {dimensions.status === 'loading' ? <LoadingState /> : null}
      {dimensions.status === 'error' ? (
        <StatePanel
          variant={dimensions.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={dimensions.error.status === 403 ? 'دسترسی ندارید' : 'قابل نمایش نیست'}
        />
      ) : null}

      {dimensions.status === 'ready' ? (
        <V2Card>
          <V2CardHeader title="سطوح تفصیلی" subtitle="مرکز هزینه، پروژه، شعبه، قرارداد و …" />
          <div style={{ padding: '0 20px 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {STANDARD_DIMENSIONS.filter((d) => !existingCodes.has(d.code)).map((d) => (
              <V2Button
                key={d.code}
                type="button"
                size="sm"
                variant="secondary"
                icon="plus"
                disabled={busy}
                onClick={() => createDimension(d.code, d.name)}
              >
                {d.name}
              </V2Button>
            ))}
          </div>
          <form
            noValidate
            onSubmit={onCreateDimension}
            style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <V2FormGrid>
              <V2FormField label="کد سطح" width="short">
                <input
                  className="sk-input ltr"
                  value={dimCode}
                  onChange={(e) => setDimCode(e.target.value)}
                />
              </V2FormField>
              <V2FormField label="نام سطح" width="wide">
                <input
                  className="sk-input"
                  value={dimName}
                  onChange={(e) => setDimName(e.target.value)}
                />
              </V2FormField>
            </V2FormGrid>
            <div>
              <V2Button type="submit" size="sm" variant="primary" icon="plus" disabled={busy}>
                تعریف سطح تفصیلی دیگر
              </V2Button>
            </div>
          </form>
          {dimensions.data.items.length === 0 ? (
            <V2EmptyState icon="info" title="هنوز سطح تفصیلی تعریف نشده" />
          ) : (
            <div
              role="tablist"
              style={{
                display: 'flex',
                gap: 6,
                padding: '12px 20px',
                borderTop: '1px solid var(--sk-divider)',
                flexWrap: 'wrap',
              }}
            >
              {dimensions.data.items.map((d) => (
                <button
                  key={d.dimensionId}
                  type="button"
                  role="tab"
                  aria-selected={activeDimension?.dimensionId === d.dimensionId}
                  className={`sk-btn sk-btn--sm ${
                    activeDimension?.dimensionId === d.dimensionId
                      ? 'sk-btn--primary'
                      : 'sk-btn--secondary'
                  }`}
                  onClick={() => setSelectedId(d.dimensionId)}
                >
                  {d.name}
                </button>
              ))}
            </div>
          )}
        </V2Card>
      ) : null}

      {activeDimension ? (
        <V2Card>
          <V2CardHeader title={`${activeDimension.name} — تعریف و فهرست`} />
          <form
            noValidate
            onSubmit={onCreateValue}
            style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <V2FormGrid>
              <V2FormField label="کد" required width="short">
                <input
                  className="sk-input ltr"
                  value={valueCode}
                  onChange={(e) => setValueCode(e.target.value)}
                />
              </V2FormField>
              <V2FormField label="نام" required width="wide">
                <input
                  className="sk-input"
                  value={valueName}
                  onChange={(e) => setValueName(e.target.value)}
                />
              </V2FormField>
            </V2FormGrid>
            <div>
              <V2Button type="submit" variant="primary" icon="plus" disabled={busy}>
                افزودن {activeDimension.name}
              </V2Button>
            </div>
          </form>
          {values.status === 'loading' ? <LoadingState /> : null}
          {values.status === 'ready' && values.data.items.length === 0 ? (
            <V2EmptyState icon="info" title="موردی تعریف نشده" />
          ) : null}
          {values.status === 'ready' && values.data.items.length > 0 ? (
            <div className="sk-table-scroll">
              <table className="sk-table sk-table--compact">
                <thead>
                  <tr>
                    <th>کد</th>
                    <th>نام</th>
                    <th>وضعیت</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {values.data.items.map((v) => (
                    <tr key={v.dimensionValueId}>
                      <td className="ltr sk-num">{v.code}</td>
                      <td>{v.name}</td>
                      <td>
                        <V2Badge tone={v.status === 'active' ? 'success' : 'neutral'}>
                          {v.status === 'active' ? 'فعال' : 'غیرفعال'}
                        </V2Badge>
                      </td>
                      <td>
                        <V2Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleValue(v)}
                        >
                          {v.status === 'active' ? 'غیرفعال‌سازی' : 'فعال‌سازی'}
                        </V2Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </V2Card>
      ) : null}

      {error ? <div style={{ fontSize: 13, color: 'var(--sk-danger)' }}>{error}</div> : null}
    </div>
  );
}
